import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import {
  categories,
  currentTask,
  calendarStatus,
  goBack,
  navigate,
  refreshCurrentTask,
  resetSkipSession,
  showToast,
  friendlyError,
  bumpTasksVersion,
} from '../store.js';
import { __ } from '../i18n.js';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';
import { formatMinutes, formatElapsed, daysUntil } from '../lib/format.js';
import { IMPACT_OPTIONS, categoryIconName } from '../lib/labels.js';
import { explainTask } from '../lib/explain.js';

/**
 * The core screen: never show more than one task. Fetches the next task for
 * the chosen category/time budget (via store.js, which already talked to
 * `flow` on the way in from TimeScreen) and lets the user start, complete,
 * or skip it.
 */
export default function TaskScreen({ params }) {
  const [submitting, setSubmitting] = useState(false);
  const [calendarBlocking, setCalendarBlocking] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    if (!currentTask.value) {
      refreshCurrentTask().catch((err) => {
        showToast(friendlyError(err), 'error');
      });
    }
  }, [params.categoryId, params.availableMinutes]);

  const result = currentTask.value;
  // Phase 5A: `started` is derived from the server's own status field, not
  // separate local component state — a task can already be in_progress on
  // mount (resumed via store.js's bootstrap() after a reload/app restart),
  // and this way there's only ever one source of truth for it.
  const started = result?.task?.status === 'in_progress';

  useEffect(() => {
    if (!started) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [started, result?.task?.id]);

  async function handleStart(taskId) {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.tasks.start(taskId);
      currentTask.value = {
        ...currentTask.value,
        task: { ...currentTask.value.task, status: 'in_progress', started_at: res.started_at },
      };
      // WP-2: GET /tasks?status=active is an exact status match, and
      // starting moves this task to 'in_progress' — it would otherwise
      // sit stale in the desktop Aankomend panel until an unrelated
      // refetch.
      bumpTasksVersion();
      showToast(__('Taak gestart.', 'etm'), 'success');
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Phase 5A one-tap "Blokkeer in agenda" (roadmap §7): opens a prefilled
   * Google Calendar event AND downloads a .ics (works with Outlook/Apple)
   * from a single fetch. The blank window is opened synchronously, before
   * the `await`, so the browser still attributes it to this click and
   * doesn't treat the later `location.href` assignment as an unrequested
   * popup — opening it only after the fetch resolves would risk that.
   */
  async function handleCalendarBlock(taskId) {
    if (calendarBlocking) return;
    setCalendarBlocking(true);
    const popup = window.open('', '_blank');
    try {
      const data = await api.tasks.calendarBlock(taskId);
      if (popup) {
        popup.location.href = data.google_calendar_url;
      }

      const blob = new Blob([data.ics_content], { type: 'text/calendar' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = data.ics_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      if (popup) popup.close();
      showToast(friendlyError(err), 'error');
    } finally {
      setCalendarBlocking(false);
    }
  }

  async function handleComplete(taskId) {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.tasks.complete(taskId);
      showToast(__('Taak voltooid!', 'etm'), 'success');
      // A completed task closes out this attempt — the 2-skip friction
      // counter (roadmap §6) shouldn't follow the user into the next one.
      resetSkipSession();
      bumpTasksVersion(); // WP-2: keep the desktop Aankomend panel in sync
      await refreshCurrentTask();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function handleSkip(taskId) {
    if (submitting) return;
    navigate('skip', { taskId });
  }

  /**
   * WP-3: the "Zet in agenda" hint line. The endpoint works without OAuth
   * (Google URL popup + .ics download for Outlook & everything else), so
   * the default hint just names both routes; when a provider is actually
   * OAuth-connected (Phase 5B), reflect that instead — the brief's
   * "reflect that in the label/toast".
   */
  function calendarProviderHint() {
    const providers = calendarStatus.value;
    const connected = [];
    if (providers?.google?.connected) connected.push('Google');
    if (providers?.microsoft?.connected) connected.push('Outlook');
    if (connected.length > 0) {
      return __('Synct met %s', 'etm').replace('%s', connected.join(' · '));
    }
    return 'Google · Outlook';
  }

  // WP-1: the leading warning emoji left these strings — the banner
  // renders a real warning icon now (same words, same string count, no
  // emoji glyph in copy).
  function warningText(warning) {
    const days = daysUntil(warning.deadline);
    if (days !== null && days > 0) {
      const template =
        days === 1
          ? __("'%s' heeft over %d dag een harde deadline maar past niet in dit tijdblok.", 'etm')
          : __("'%s' heeft over %d dagen een harde deadline maar past niet in dit tijdblok.", 'etm');
      return template.replace('%s', warning.title).replace('%d', days);
    }
    if (days === 0) {
      return __("'%s' heeft vandaag een harde deadline maar past niet in dit tijdblok.", 'etm').replace(
        '%s',
        warning.title
      );
    }
    return __("'%s' is te laat en past niet in dit tijdblok.", 'etm').replace('%s', warning.title);
  }

  if (!result) {
    return (
      <>
        <BackButton onClick={goBack} />
        <p class="etm-screen-subtitle">{__('Bezig met laden…', 'etm')}</p>
      </>
    );
  }

  const warnings = result.warnings || [];

  if (!result.task) {
    return (
      <>
        <BackButton onClick={goBack} />
        {warnings.map((warning) => (
          <div class="etm-warning-banner" key={warning.id}>
            <Icon name="warning" size={20} />
            <span>{warningText(warning)}</span>
          </div>
        ))}
        <div class="etm-empty-state">
          <span class="icon">
            <Icon name={result.outdoor_suppressed ? 'rainy' : 'celebration'} size={44} />
          </span>
          <p>
            {result.outdoor_suppressed
              ? __('Buitentaken zijn verborgen vanwege het weer. Kies een andere categorie.', 'etm')
              : __('Geen taken passen in dit tijdblok. Mooi zo!', 'etm')}
          </p>
          <button type="button" class="etm-btn etm-btn-secondary" onClick={goBack}>
            {__('Andere tijd kiezen', 'etm')}
          </button>
        </div>
      </>
    );
  }

  const task = result.task;
  const category = categories.value.find((c) => c.id === task.category_id);
  const impactOption = IMPACT_OPTIONS.find((o) => o.value === Number(task.impact));
  const remainingCount = Number(result.remaining_count) || 0;

  return (
    <>
      <BackButton onClick={goBack} />

      {warnings.map((warning) => (
        <div class="etm-warning-banner" key={warning.id}>
          <Icon name="warning" size={20} />
          <span>{warningText(warning)}</span>
        </div>
      ))}

      <div class="etm-task-card">
        <div class="etm-task-card__category">
          {category ? (
            <>
              <Icon name={categoryIconName(category.icon)} size={16} /> {category.name}
            </>
          ) : (
            __('Taak', 'etm')
          )}
        </div>

        <h2 class="etm-task-card__title">{task.title}</h2>

        {task.description ? <p class="etm-task-card__description">{task.description}</p> : null}

        <div class="etm-task-card__meta">
          <span class="etm-chip">{formatMinutes(task.effort_minutes)}</span>
          {impactOption ? <span class="etm-chip">{impactOption.label()}</span> : null}
          {task.reward ? (
            <span class="etm-badge-reward">
              <Icon name="redeem" size={16} /> {task.reward}
            </span>
          ) : null}
        </div>

        <details class="etm-task-card__why">
          <summary>
            {__('Waarom deze taak?', 'etm')}
            <Icon name="keyboard_arrow_down" size={18} />
          </summary>
          <ul>
            {explainTask(task, params.availableMinutes).map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </details>
      </div>

      {started ? (
        <div class="etm-timer">
          <Icon name="timer" size={20} /> {formatElapsed(task.started_at, nowTick)} {__('bezig', 'etm')}
        </div>
      ) : null}

      {remainingCount > 0 ? (
        <div class="etm-remaining-hint">
          {(remainingCount === 1
            ? __('%d andere taak past ook', 'etm')
            : __('%d andere taken passen ook', 'etm')
          ).replace('%d', remainingCount)}
        </div>
      ) : null}

      {/* WP-3: pre-start, the two "what happens with this task" choices sit
          side by side — do it now (Start nu) or plan it (Zet in agenda,
          the existing calendarBlock flow: Google-URL popup + .ics download,
          deliberately NOT starting the timer). Once started, the row
          disappears and Voltooien becomes the primary action. */}
      {!started ? (
        <div class="etm-task-actions-row">
          <button
            type="button"
            class="etm-btn etm-btn-primary"
            disabled={submitting}
            onClick={() => handleStart(task.id)}
          >
            <Icon name="play_arrow" size={22} /> {__('Start nu', 'etm')}
          </button>
          <button
            type="button"
            class="etm-btn etm-btn-secondary"
            disabled={calendarBlocking}
            onClick={() => handleCalendarBlock(task.id)}
          >
            <span class="etm-btn__label">
              <Icon name="calendar_month" size={20} />{' '}
              {calendarBlocking ? __('Bezig…', 'etm') : __('Zet in agenda', 'etm')}
            </span>
            <span class="etm-btn__hint">{calendarProviderHint()}</span>
          </button>
        </div>
      ) : null}

      <div class="etm-task-actions">
        <button
          type="button"
          class={`etm-btn ${started ? 'etm-btn-primary' : 'etm-btn-secondary'}`}
          disabled={submitting}
          onClick={() => handleComplete(task.id)}
        >
          <Icon name="check" size={20} /> {__('Voltooien', 'etm')}
        </button>
        <button
          type="button"
          class="etm-btn etm-btn-secondary"
          disabled={submitting}
          onClick={() => handleSkip(task.id)}
        >
          <Icon name="skip_next" size={20} /> {__('Overslaan', 'etm')}
        </button>
      </div>

      {started ? (
        <button
          type="button"
          class="etm-btn etm-btn-ghost"
          disabled={calendarBlocking}
          onClick={() => handleCalendarBlock(task.id)}
        >
          <Icon name="calendar_month" size={20} />{' '}
          {calendarBlocking ? __('Bezig…', 'etm') : __('Blokkeer in agenda', 'etm')}
        </button>
      ) : null}
    </>
  );
}
